import { useCallback, useEffect, useMemo, useState } from 'react';

import { decodeDesign } from '../engine/serialize';
import { buildGuide } from '../engine/guide';
import { PatternChart } from './Preview';
import { hrefFor } from './router';
import { clearProgress, designId, loadProgress, saveProgress } from '../state/storage';
import type { Settings } from '../state/storage';

interface Props {
  code: string;
  settings: Settings;
}

/**
 * One knot at a time, on a phone, with a bundle of floss taped to a clipboard.
 *
 * Design constraints that drove this screen:
 *  - She is not looking at it continuously. It has to answer "where was I"
 *    instantly, from across a room, with one glance.
 *  - Her hands are full. The advance target is the whole lower half of the
 *    screen, not a small button.
 *  - She will close the tab. Progress is written on every single step, not on
 *    unload, because phone browsers kill background tabs without warning.
 */
export function KnotAlong({ code, settings }: Props) {
  const design = useMemo(() => {
    try {
      return decodeDesign(code);
    } catch {
      return null;
    }
  }, [code]);

  const guide = useMemo(() => (design ? buildGuide(design, settings.gauge) : null), [design, settings.gauge]);
  const id = useMemo(() => designId(code), [code]);

  const [done, setDone] = useState(() => loadProgress(designId(code))?.done ?? 0);
  const [showChart, setShowChart] = useState(true);

  // Persist on every change. Cheap, and it is the whole point of the screen.
  useEffect(() => {
    if (!design || !guide) return;
    saveProgress({
      id,
      done,
      updatedAt: Date.now(),
      name: design.name,
      totalSteps: guide.totalKnots,
    });
  }, [design, guide, id, done]);

  const total = guide?.totalKnots ?? 0;
  const advance = useCallback(() => setDone((d) => Math.min(total, d + 1)), [total]);
  const back = useCallback(() => setDone((d) => Math.max(0, d - 1)), []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === ' ' || e.key === 'ArrowRight' || e.key === 'Enter') {
        e.preventDefault();
        advance();
      } else if (e.key === 'ArrowLeft' || e.key === 'Backspace') {
        e.preventDefault();
        back();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [advance, back]);

  if (!design || !guide) {
    return (
      <div className="screen">
        <section className="card">
          <h2>That design link is broken</h2>
          <a className="primary as-button" href="#/">
            Start again
          </a>
        </section>
      </div>
    );
  }

  const finished = done >= total;
  const step = finished ? guide.steps[total - 1]! : guide.steps[done]!;
  const group = guide.groups.find((g) => step.n >= g.firstStep && step.n <= g.lastStep);
  const pct = total === 0 ? 0 : Math.round((done / total) * 100);
  const color = design.palette[step.color];

  return (
    <div className="knotalong">
      <header className="knotalong-top">
        <a className="link" href={hrefFor({ name: 'design', code })}>
          ← {design.name}
        </a>
        <span className="muted small">
          {done} / {total} knots · {pct}%
        </span>
      </header>

      <div className="progressbar" role="progressbar" aria-valuenow={done} aria-valuemin={0} aria-valuemax={total}>
        <span style={{ width: `${pct}%` }} />
      </div>

      {finished ? (
        <div className="knotalong-body knotalong-body--done">
          <h2>Finished — all {total.toLocaleString()} knots.</h2>
          <p>
            Tie off the ends: gather the strings at each end, plait or twist them, and knot. Trim to
            about {Math.round(design.sizing.wristCm / 2)} cm of tail per side.
          </p>
          <div className="row">
            <button type="button" className="secondary" onClick={back}>
              Back one knot
            </button>
            <button
              type="button"
              className="secondary"
              onClick={() => {
                clearProgress(id);
                setDone(0);
              }}
            >
              Start over
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="knotalong-body">
            <p className="knotalong-where">
              {group?.label ?? ''} · knot {step.indexInGroup} of {step.groupSize}
            </p>
            <div className="knotalong-headline">
              {step.knot && <span className="knotalong-glyph">{glyphFor(step)}</span>}
              <span className="knotalong-swatch" style={{ background: color?.hex }} aria-hidden="true" />
              <div>
                <p className="knotalong-short">{step.short}</p>
                <p className="knotalong-color">
                  DMC {color?.dmc} · {color?.name}
                </p>
              </div>
            </div>
            <p className="knotalong-text">{step.text}</p>
            {group?.note && <p className="note">{group.note}</p>}
          </div>

          {showChart && (
            <div className="knotalong-chart">
              <ChartWindow code={code} settings={settings} step={step.n} />
            </div>
          )}

          <button type="button" className="knotalong-advance" onClick={advance}>
            <span>Knot {step.n} done</span>
            <em>tap anywhere here, or press space</em>
          </button>

          <footer className="knotalong-bottom">
            <button type="button" className="secondary" onClick={back} disabled={done === 0}>
              Undo
            </button>
            <button type="button" className="secondary" onClick={() => setShowChart((s) => !s)}>
              {showChart ? 'Hide chart' : 'Show chart'}
            </button>
            <button
              type="button"
              className="secondary"
              onClick={() => {
                if (window.confirm('Forget your place and start this bracelet from knot 1?')) {
                  clearProgress(id);
                  setDone(0);
                }
              }}
            >
              Reset
            </button>
          </footer>
        </>
      )}
    </div>
  );
}

function glyphFor(step: { knot?: string }): string {
  const map: Record<string, string> = { F: '/', B: '\\', FB: '>', BF: '<' };
  return step.knot ? (map[step.knot] ?? '•') : '•';
}

/**
 * A scrolling window onto the chart, kept centred on the current knot so she
 * can see a few rows either side without hunting.
 */
function ChartWindow({ code, settings, step }: { code: string; settings: Settings; step: number }) {
  const design = useMemo(() => decodeDesign(code), [code]);
  const guide = useMemo(() => buildGuide(design, settings.gauge), [design, settings.gauge]);
  const current = guide.steps[Math.min(step, guide.steps.length) - 1];
  const groupIndex = current?.group ?? 0;

  useEffect(() => {
    const el = document.querySelector('.knotalong-chart .chart');
    const parent = el?.parentElement;
    if (!parent || !el) return;
    // Scroll so the active row sits about a third down the visible window.
    const rows = Math.max(1, guide.totalGroups);
    const target = ((groupIndex + 0.5) / rows) * el.clientHeight - parent.clientHeight / 3;
    parent.scrollTop = Math.max(0, target);
  }, [groupIndex, guide.totalGroups]);

  return <PatternChart design={design} highlightStep={step} cell={16} />;
}
